import React from 'react';
import {Controller} from 'react-hook-form';
import {defineMessages, useIntl} from 'react-intl';
import {Button} from '@igp/shared/components/button';
import {Form, FormControlGroup} from '@igp/shared/components/form';
import {StyledInput} from '@igp/shared/components/input';
import {ModalFooter} from '@igp/shared/components/modal';
import {EmptyPlaceholder, Select} from '@igp/shared/components/select';
import {AalFilter} from '@igp/shared/features/bo-user-group';
import {licenseOptions, type LicenseType} from '@igp/shared/features/brand-license';

import {useReferralLinkConfigurationEdit} from '../hooks';
import type {ReferralLinkConfigViewModel} from '../types';
import {validateUrlWithoutQueryParams} from '../utils';

const localized = defineMessages({
    cancel: {
        id: 'EditReferralLinkConfigModal_cancel',
        defaultMessage: 'Cancel',
    },
    confirm: {
        id: 'EditReferralLinkConfigModal_confirm',
        defaultMessage: 'Confirm',
    },
    aalGroup: {
        id: 'EditReferralLinkConfigModal_aalGroup',
        defaultMessage: 'AAL Group',
    },
    referralLinkBaseUrl: {
        id: 'EditReferralLinkConfigModal_referralLinkBaseUrl',
        defaultMessage: 'Referral Link Base URL',
    },
    license: {
        id: 'EditReferralLinkConfigModal_license',
        defaultMessage: 'License',
    },
    required: {
        id: 'EditReferralLinkConfigModal_required',
        defaultMessage: 'Field is required',
    },
    invalidUrl: {
        id: 'EditReferralLinkConfigModal_invalidUrl',
        defaultMessage: 'Must be a valid URL without query parameters',
    },
});

type EditReferralLinkConfigModalProps = {
    initialModel: ReferralLinkConfigViewModel | null;
    onSave?: () => void;
    onCancel?: () => void;
};

export function EditReferralLinkConfigModal({initialModel, onSave, onCancel}: EditReferralLinkConfigModalProps) {
    const {formatMessage} = useIntl();
    const isEditMode = initialModel !== null;

    const {
        control,
        handleSubmit,
        formState: {isValid},
        submit,
        state: {isProgress},
    } = useReferralLinkConfigurationEdit({
        initialModel,
        isEditMode,
        onSuccess: onSave,
    });

    return (
        <Form onSubmit={handleSubmit(submit)}>
            <FormControlGroup label={formatMessage(localized.aalGroup)} labelVariant="default" labelPlacement="left" key="aalGroup">
                <Controller
                    render={({field}) => (
                        <AalFilter
                            value={[field.value]}
                            onChange={value => field.onChange(value?.length > 0 ? value : '')}
                            disabled={isEditMode}
                            fullWidthInput
                            hasLabel={false}
                            filterBy="group_name"
                        />
                    )}
                    name="aal_group_name"
                    control={control}
                    rules={{
                        required: formatMessage(localized.required),
                    }}
                />
            </FormControlGroup>
            <FormControlGroup label={formatMessage(localized.license)} labelVariant="default" labelPlacement="left" key="license">
                <Controller
                    render={({field}) => (
                        <Select
                            options={licenseOptions}
                            value={field.value}
                            onSubmit={(value: LicenseType[]) => field.onChange(value?.[0])}
                            disabled={isEditMode}
                            fullWidthInput
                            emptyPlaceholder={EmptyPlaceholder.All}
                            showResetButton
                        />
                    )}
                    name="license"
                    control={control}
                />
            </FormControlGroup>
            <FormControlGroup
                label={formatMessage(localized.referralLinkBaseUrl)}
                labelVariant="default"
                labelPlacement="left"
                key="referralLinkBaseUrl"
            >
                <Controller
                    render={({field, fieldState}) => (
                        <StyledInput
                            value={field.value}
                            onChange={value => field.onChange(value)}
                            onBlur={field.onBlur}
                            fieldState={fieldState}
                            hasBottomSpacing={false}
                            placeholder="https://example.com/some-page"
                        />
                    )}
                    name="referral_link_base_url"
                    control={control}
                    rules={{
                        required: formatMessage(localized.required),
                        validate: value => (validateUrlWithoutQueryParams(value) ? true : formatMessage(localized.invalidUrl)),
                    }}
                />
            </FormControlGroup>
            <ModalFooter>
                <Button color="secondary" variant="contained" label={localized.cancel} onClick={onCancel} />
                <Button type="submit" color="primary" variant="contained" label={localized.confirm} disabled={!isValid || isProgress} />
            </ModalFooter>
        </Form>
    );
}
